/**
 * The location picker's options for one game (spec §8.6).
 *
 * Choosing an option sets `venueId` AND `field` together. That pairing is the
 * point: `composeFieldKey` collides only on an exact normalized match, so
 * free-typing "Fld 1" on one game and "Field 1" on another would silently defeat
 * the engine's field-clash check. Ad-hoc free text stays available via Custom.
 */
import { gameLocation } from "./format";
import type { Venue } from "./types";

export interface LocationOption {
  /** Stable `<option value>` — see `locationKey`. */
  key: string;
  /** `<optgroup label>`, or null for the ungrouped leading/trailing entries. */
  group: string | null;
  label: string;
  venueId?: string;
  field?: string;
}

export const TBD_KEY = "tbd";
export const CUSTOM_KEY = "custom";

/**
 * Content-derived, NOT index-derived: a caller holds a chosen key in state while
 * the option list is rebuilt on every render, so an index would silently point at
 * a different venue's field the moment the tournament's venue list changes. Ids
 * are ULIDs and contain no "|", so the pair round-trips unambiguously and two
 * distinct venue/field pairs can never collide.
 */
const locationKey = (venueId: string, field?: string) => `v:${venueId}|${field ?? ""}`;

/**
 * Options for the venues in `venueIds`, in that order. Callers pass the
 * tournament's attached venues union the game's own `venueId`, so a pre-existing
 * assignment is always representable. Unknown and repeated ids are skipped.
 */
export function locationOptions(venues: Venue[], venueIds: string[]): LocationOption[] {
  const options: LocationOption[] = [{ key: TBD_KEY, group: null, label: "Location TBD" }];
  const seen = new Set<string>();

  for (const id of venueIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    const venue = venues.find((v) => v.id === id);
    if (!venue) continue;

    for (const f of venue.fields) {
      options.push({
        key: locationKey(venue.id, f.name),
        group: venue.name,
        label: f.name,
        venueId: venue.id,
        field: f.name,
      });
    }
    // A venue may legitimately have no fields listed, and "somewhere at this
    // venue" is a real answer even when it does.
    options.push({
      key: locationKey(venue.id),
      group: venue.name,
      label: "— whole venue —",
      venueId: venue.id,
    });
  }

  options.push({ key: CUSTOM_KEY, group: null, label: "Custom…" });
  return options;
}

/** The key of the option matching a game, or CUSTOM_KEY for ad-hoc free text. */
export function matchLocation(
  options: LocationOption[],
  game: { venueId?: string; field?: string },
): string {
  const venueId = game.venueId || undefined;
  const field = game.field || undefined;
  return options.find((o) => o.venueId === venueId && o.field === field)?.key ?? CUSTOM_KEY;
}

/** Options in render order, with consecutive same-venue entries collected. */
export function groupLocationOptions(
  options: LocationOption[],
): { group: string | null; options: LocationOption[] }[] {
  const groups: { group: string | null; options: LocationOption[] }[] = [];
  for (const o of options) {
    const last = groups[groups.length - 1];
    if (last && last.group === o.group && o.group !== null) last.options.push(o);
    else groups.push({ group: o.group, options: [o] });
  }
  return groups;
}

/**
 * The text a game's place reads as — "Riverside · Field 1", "Field 1" for an
 * ad-hoc site, "" for a game placed nowhere yet. The Scores page keys its field
 * chips on this and prints it on every card, so the same helper answers both.
 */
export function venueLabel(venues: Venue[], game: { venueId?: string; field?: string }): string {
  return gameLocation(venues, game)?.text ?? "";
}
